import React from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FeaturesTab } from "./components/FeaturesTab";
import { PlansTab } from "./components/PlansTab";
import { TransactionsTab } from "./components/TransactionsTab";
import { SubscriptionOverviewTab } from "./components/SubscriptionOverviewTab";

enum SubscriptionsPageTab {
  OVERVIEW = "overview",
  SUBSCRIPTIONS = "subscriptions",
  FEATURES = "features",
  TRANSACTIONS = "transactions",
}

const isSubscriptionsPageTab = (value: string): value is SubscriptionsPageTab =>
  value === SubscriptionsPageTab.OVERVIEW ||
  value === SubscriptionsPageTab.SUBSCRIPTIONS ||
  value === SubscriptionsPageTab.FEATURES ||
  value === SubscriptionsPageTab.TRANSACTIONS;

export const SubscriptionsPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const rawTab = searchParams.get("tab") ?? "";
  const activeTab = isSubscriptionsPageTab(rawTab)
    ? rawTab
    : SubscriptionsPageTab.OVERVIEW;

  const handleTabChange = (value: string) => {
    if (isSubscriptionsPageTab(value)) {
      setSearchParams({ tab: value });
    }
  };

  return (
    <div className="mx-auto flex max-w-[1344px] flex-col space-y-6 p-12">
      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="w-full"
      >
        <div className="mb-6 flex items-center justify-center">
          <TabsList className="h-auto space-x-4 bg-transparent p-0">
            <TabsTrigger
              value={SubscriptionsPageTab.OVERVIEW}
              className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 data-[state=active]:bg-[#D8D8D8] data-[state=active]:text-black"
            >
              {t("subscriptions.tabs.overview")}
            </TabsTrigger>
            <TabsTrigger
              value={SubscriptionsPageTab.SUBSCRIPTIONS}
              className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 data-[state=active]:bg-[#D8D8D8] data-[state=active]:text-black"
            >
              {t("subscriptions.tabs.plans")}
            </TabsTrigger>
            <TabsTrigger
              value={SubscriptionsPageTab.FEATURES}
              className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 data-[state=active]:bg-[#D8D8D8] data-[state=active]:text-black"
            >
              {t("subscriptions.tabs.features")}
            </TabsTrigger>
            <TabsTrigger
              value={SubscriptionsPageTab.TRANSACTIONS}
              className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 data-[state=active]:bg-[#D8D8D8] data-[state=active]:text-black"
            >
              {t("subscriptions.tabs.transactions")}
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value={SubscriptionsPageTab.OVERVIEW} className="mt-0">
          <SubscriptionOverviewTab
            enabled={activeTab === SubscriptionsPageTab.OVERVIEW}
          />
        </TabsContent>

        <TabsContent
          value={SubscriptionsPageTab.SUBSCRIPTIONS}
          className="mt-0"
        >
          <PlansTab
            enabled={activeTab === SubscriptionsPageTab.SUBSCRIPTIONS}
          />
        </TabsContent>

        <TabsContent value={SubscriptionsPageTab.FEATURES} className="mt-0">
          <FeaturesTab enabled={activeTab === SubscriptionsPageTab.FEATURES} />
        </TabsContent>

        <TabsContent value={SubscriptionsPageTab.TRANSACTIONS} className="mt-0">
          <TransactionsTab
            enabled={activeTab === SubscriptionsPageTab.TRANSACTIONS}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};
